// dependencies
import React from "react";

import { faCheckCircle } from "@fortawesome/pro-duotone-svg-icons";
import { faUserClock } from "@fortawesome/pro-regular-svg-icons";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import AuthorResult from "./AuthorResult";

// styles
import css from "../AddFormModal.module.scss";

type Author = {
	_id: string;
	name: string;
};

type FormResult = {
	author: Author;
	review_report_id?: string;
	reason?: string;
};

type AddFormResults = {
	forms_added: FormResult[];
	forms_not_added: FormResult[];
	forms_require_approval: FormResult[];
};

type Review = {
	id?: string | null;
	name?: string | null;
};

type Form = {
	id?: string;
	name?: string;
	easyAddConfigID?: string;
	whoCanAddForm?: {
		any_employee: boolean;
		subject: boolean;
		manager_or_chain_of_command: boolean;
		admin_or_group_admin: boolean;
	};
	subject?: string;
	allowComments?: boolean;
};

type AddFormData = {
	review?: Review;
	form?: Form;
	authors?: { id: string; name: string; noEdit?: boolean }[];
	formComment?: string;
	characterCount?: number;
};

type ResultsProps = {
	results: AddFormResults;
	addFormData: AddFormData;
};

const Results = ({ results, addFormData }: ResultsProps) => {
	return (
		<div>
			<div
				className={css.resultsHeader}
			>{`Adding ${addFormData.form?.name || ""} to ${addFormData.review?.name || ""}...`}</div>
			{results.forms_added.length ? (
				<div className={css.resultTypeContainer}>
					<div className={css.resultsHeadingContainer}>
						<div className={`${css.resultIcon} ${css.success}`}>
							<FontAwesomeIcon icon={faCheckCircle} />
						</div>
						<div className={css.resultTitle}>{`Successfully added form${
							results.forms_added.length > 1 ? "s" : ""
						} for:`}</div>
					</div>
					<div className={css.resultsContainer}>
						{results.forms_added.map((form) => (
							<AuthorResult
								key={form.author._id}
								authorID={form.author._id}
								authorName={form.author.name}
								openLink={`/review/${form.review_report_id}`}
							/>
						))}
					</div>
				</div>
			) : null}

			{results.forms_not_added.length ? (
				<div className={css.resultTypeContainer}>
					<div className={css.resultsHeadingContainer}>
						<div className={`${css.resultIcon} ${css.failed}`}>
							<FontAwesomeIcon icon={faTimes} />
						</div>
						<div className={css.resultTitle}>{`Could not add form${
							results.forms_not_added.length > 1 ? "s" : ""
						} for:`}</div>
					</div>
					<div className={css.resultsContainer}>
						{results.forms_not_added.map((form) => (
							<AuthorResult
								key={form.author._id}
								authorID={form.author._id}
								authorName={form.author.name}
								failedReason={form.reason}
							/>
						))}
					</div>
				</div>
			) : null}

			{results.forms_require_approval.length ? (
				<div className={css.resultTypeContainer}>
					<div className={css.resultsHeadingContainer}>
						<div className={`${css.resultIcon} ${css.warning}`}>
							<FontAwesomeIcon icon={faUserClock} />
						</div>
						<div className={css.resultTitle}>Submitted for approval:</div>
					</div>
					<div className={css.resultsContainer}>
						{results.forms_require_approval.map((form) => (
							<AuthorResult
								key={form.author._id}
								authorID={form.author._id}
								authorName={form.author.name}
							/>
						))}
					</div>
				</div>
			) : null}
		</div>
	);
};

export default Results;
